import {
    BASE_GROUND_OFFSET,
    BEDROCK_Y,
    CAVE_MIN_DEPTH_BELOW_TOP,
    CAVE_MIN_Y_ABOVE_BEDROCK,
    CAVE_COUNT_MIN,
    CAVE_COUNT_MAX,
    CAVE_RADIUS_MIN,
    CAVE_RADIUS_MAX,
    CAVE_VRADIUS_MIN,
    CAVE_VRADIUS_MAX,
    CAVE_TUNNEL_PROBABILITY,
    CAVE_TUNNEL_RADIUS,
    CAVE_ENTRANCE_RADIUS,
} from "../config";
import { BLOCK, type VoxelWorld } from "../types";

function mulberry32(seed: number) {
    return function () {
        let t = (seed += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

interface Room {
    x: number;
    y: number;
    z: number;
    rx: number;
    ry: number;
    rz: number;
}

/**
 * Air-out a single cell only if it's currently solid stone AND sits within the
 * safe band (above bedrock buffer, below surface buffer for that column). All
 * carving routes through here so no tunnel can punch through to daylight.
 */
function tryCarve(world: VoxelWorld, heights: number[][], x: number, z: number, y: number) {
    if (x < 0 || z < 0 || x >= world.size || z >= world.size) return;
    const topAbs = BASE_GROUND_OFFSET + heights[x][z];
    if (y < BEDROCK_Y + CAVE_MIN_Y_ABOVE_BEDROCK) return;
    if (y > topAbs - CAVE_MIN_DEPTH_BELOW_TOP) return;
    if (world.get(x, y, z) !== BLOCK.Stone) return;
    world.set(x, y, z, BLOCK.Empty);
}

function carveEllipsoid(world: VoxelWorld, heights: number[][], room: Room) {
    const { x, y, z, rx, ry, rz } = room;
    const xMin = Math.max(0, Math.floor(x - rx));
    const xMax = Math.min(world.size - 1, Math.ceil(x + rx));
    const zMin = Math.max(0, Math.floor(z - rz));
    const zMax = Math.min(world.size - 1, Math.ceil(z + rz));
    const yMin = Math.floor(y - ry);
    const yMax = Math.ceil(y + ry);
    for (let xi = xMin; xi <= xMax; xi++) {
        for (let zi = zMin; zi <= zMax; zi++) {
            for (let yi = yMin; yi <= yMax; yi++) {
                const dx = (xi - x) / rx;
                const dy = (yi - y) / ry;
                const dz = (zi - z) / rz;
                if (dx * dx + dy * dy + dz * dz <= 1) {
                    tryCarve(world, heights, xi, zi, yi);
                }
            }
        }
    }
}

function carveSphere(world: VoxelWorld, heights: number[][], cx: number, cy: number, cz: number, r: number) {
    const xMin = Math.max(0, Math.floor(cx - r));
    const xMax = Math.min(world.size - 1, Math.ceil(cx + r));
    const zMin = Math.max(0, Math.floor(cz - r));
    const zMax = Math.min(world.size - 1, Math.ceil(cz + r));
    const yMin = Math.floor(cy - r);
    const yMax = Math.ceil(cy + r);
    const r2 = r * r;
    for (let xi = xMin; xi <= xMax; xi++) {
        for (let zi = zMin; zi <= zMax; zi++) {
            for (let yi = yMin; yi <= yMax; yi++) {
                const dx = xi - cx;
                const dy = yi - cy;
                const dz = zi - cz;
                if (dx * dx + dy * dy + dz * dz <= r2) {
                    tryCarve(world, heights, xi, zi, yi);
                }
            }
        }
    }
}

/**
 * Walk a sphere brush in small steps from a → b, jittered slightly so the
 * tunnel is not a perfect line. Each step calls tryCarve which gates against
 * surface/bedrock: a tunnel near the world skin simply leaves stone intact.
 */
function carveTunnel(
    world: VoxelWorld,
    heights: number[][],
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    rng: () => number,
) {
    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const steps = Math.max(2, Math.ceil(len * 1.5));
    for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const jx = (rng() - 0.5) * 1.2;
        const jy = (rng() - 0.5) * 0.8;
        const jz = (rng() - 0.5) * 1.2;
        const x = ax + dx * t + jx;
        const y = ay + dy * t + jy;
        const z = az + dz * t + jz;
        carveSphere(world, heights, x, y, z, CAVE_TUNNEL_RADIUS);
    }
}

export function carveCaves(world: VoxelWorld, heights: number[][], seed: number): void {
    const rng = mulberry32(seed * 9.81 + 73);
    const N = world.size;

    const count = CAVE_COUNT_MIN + Math.floor(rng() * (CAVE_COUNT_MAX - CAVE_COUNT_MIN + 1));
    const rooms: Room[] = [];

    for (let i = 0; i < count; i++) {
        // Keep room centers a few cells in from edges so ellipsoid stays inside grid.
        const margin = CAVE_RADIUS_MAX + 1;
        const x = margin + Math.floor(rng() * (N - margin * 2));
        const z = margin + Math.floor(rng() * (N - margin * 2));

        // Pick y inside the safe band of THIS column. minTop = topAbs - buffer.
        const topAbs = BASE_GROUND_OFFSET + heights[x][z];
        const yMin = BEDROCK_Y + CAVE_MIN_Y_ABOVE_BEDROCK + CAVE_VRADIUS_MAX;
        const yMax = topAbs - CAVE_MIN_DEPTH_BELOW_TOP - CAVE_VRADIUS_MAX;
        if (yMax <= yMin) continue;
        const y = yMin + Math.floor(rng() * (yMax - yMin + 1));

        const rx = CAVE_RADIUS_MIN + Math.floor(rng() * (CAVE_RADIUS_MAX - CAVE_RADIUS_MIN + 1));
        const rz = CAVE_RADIUS_MIN + Math.floor(rng() * (CAVE_RADIUS_MAX - CAVE_RADIUS_MIN + 1));
        const ry = CAVE_VRADIUS_MIN + Math.floor(rng() * (CAVE_VRADIUS_MAX - CAVE_VRADIUS_MIN + 1));

        const room: Room = { x, y, z, rx, ry, rz };
        rooms.push(room);
        carveEllipsoid(world, heights, room);
    }

    // Optionally connect ONE pair of rooms.
    if (rooms.length >= 2 && rng() < CAVE_TUNNEL_PROBABILITY) {
        const a = rooms[0];
        const b = rooms[1];
        carveTunnel(world, heights, a.x, a.y, a.z, b.x, b.y, b.z, rng);
    }

    // Always cut an entrance tunnel from the first room to the closest chunk
    // edge so the cave is visible in the isometric view. Force the exit toward
    // the middle of an edge (never near a corner) so the mouth reads as a
    // cave and not a notched chunk corner.
    if (rooms.length > 0) {
        const r = rooms[0];
        // Camera sits at [+1,+1,+1] looking at origin, so the visible chunk
        // faces are X=0 and Z=0 (the faces whose outward normal points toward
        // the camera). Always exit through one of those, exits on the far
        // sides would be hidden behind the chunk.
        const distLeft = r.x;
        const distFront = r.z;
        const cornerBuffer = Math.floor(N * 0.28);
        const clampMid = (v: number) => Math.max(cornerBuffer, Math.min(N - 1 - cornerBuffer, v));
        let tx: number;
        let tz: number;
        if (distLeft <= distFront) {
            tx = 0;
            tz = clampMid(r.z);
        } else {
            tz = 0;
            tx = clampMid(r.x);
        }
        carveTunnelWithRadius(world, heights, r.x, r.y, r.z, tx, r.y, tz, rng, CAVE_ENTRANCE_RADIUS);
        // Stack 3 spheres at the mouth so the opening reads clearly as a cave
        // arch rather than a small dot.
        carveSphere(world, heights, tx, r.y, tz, CAVE_ENTRANCE_RADIUS + 0.8);
        carveSphere(world, heights, tx, r.y + 1, tz, CAVE_ENTRANCE_RADIUS + 0.4);
        carveSphere(world, heights, tx, r.y - 1, tz, CAVE_ENTRANCE_RADIUS + 0.6);
        // Lay dirt on the cave floor near the mouth so the interior reads as
        // a darker recess against the surrounding stone.
        paintCaveFloor(world, heights, tx, r.y, tz, CAVE_ENTRANCE_RADIUS + 1.5);
        paintCaveFloor(world, heights, r.x, r.y, r.z, r.rx + 0.5);
    }
}

/**
 * Replace the lowest air cell in each (x,z) column near the mouth with dirt,
 * giving the cave interior a darker brown floor against the grey stone walls.
 */
function paintCaveFloor(
    world: VoxelWorld,
    heights: number[][],
    cx: number,
    cy: number,
    cz: number,
    radius: number,
) {
    const r = Math.ceil(radius);
    const r2 = radius * radius;
    for (let dx = -r; dx <= r; dx++) {
        for (let dz = -r; dz <= r; dz++) {
            if (dx * dx + dz * dz > r2) continue;
            const x = cx + dx;
            const z = cz + dz;
            if (x < 0 || z < 0 || x >= world.size || z >= world.size) continue;
            const topAbs = BASE_GROUND_OFFSET + heights[x][z];
            for (let y = cy + 1; y >= Math.max(1, cy - 2); y--) {
                if (y >= topAbs) continue;
                if (world.get(x, y, z) !== BLOCK.Empty) continue;
                // Cell is air; if there's stone (or air) below, drop a dirt floor.
                const below = world.get(x, y - 1, z);
                if (below === BLOCK.Stone || below === BLOCK.Empty) {
                    world.set(x, y - 1, z, BLOCK.Bedrock);
                    break;
                }
            }
        }
    }
}

function carveTunnelWithRadius(
    world: VoxelWorld,
    heights: number[][],
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    rng: () => number,
    radius: number,
) {
    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const steps = Math.max(2, Math.ceil(len * 1.5));
    for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const jx = (rng() - 0.5) * 1.0;
        const jy = (rng() - 0.5) * 0.6;
        const jz = (rng() - 0.5) * 1.0;
        const x = ax + dx * t + jx;
        const y = ay + dy * t + jy;
        const z = az + dz * t + jz;
        carveSphere(world, heights, x, y, z, radius);
    }
}
